import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SmartTable from "components/smart-table/smart-table";
import { FilterSetController } from "@weezevent/nacre";
import useCheckpoints from "pages/attendee-details/hooks/useCheckpoints";
import { SORTING } from "constants/sorting";
import Status from "components/ui/status";
import { ACTIVE_STATUS } from "models/checkpoint";
import TextTableCell from "components/text-table-cell/textTableCell";
import { isNumberOfAccessUnlimited } from "utils/checkpoints";

const ChecklistsTable = ({ filters, setFilters, eventId }) => {
	const { t } = useTranslation();

	const { data: checkpointsResult, isLoading } = useCheckpoints({
		search: filters.search,
		queryParams: {
			archived: false
		},
		eventId,
		select: checkpoints =>
			checkpoints?.map(checkpoint => ({
				key: checkpoint.id,
				name: checkpoint.name,
				defaultNbAccess: checkpoint.default_nb_access,
				strictNbAccess: checkpoint.strict_nb_access,
				isUsingStrictMode: checkpoint.is_using_strict_mode,
				status: checkpoint.status,
				checkpoint: checkpoint
			})) || []
	});

	const sortedData = React.useMemo(() => {
		if (!checkpointsResult?.data || !filters.sorting)
			return checkpointsResult?.data || [];

		return checkpointsResult.data.sort((a, b) => {
			if (filters.sorting.column === "name") {
				const aName = a.name || "";
				const bName = b.name || "";
				if (aName > bName) {
					return filters.sorting.direction === SORTING.DIRECTIONS.ASC
						? 1
						: -1;
				}
				return filters.sorting.direction === SORTING.DIRECTIONS.ASC
					? -1
					: 1;
			}

			if (filters.sorting.column === "status") {
				const aActive = a.status === ACTIVE_STATUS ? 1 : 0;
				const bActive = b.status === ACTIVE_STATUS ? 1 : 0;
				if (filters.sorting.direction === SORTING.DIRECTIONS.ASC) {
					return bActive - aActive;
				} else {
					return aActive - bActive;
				}
			}

			return 0;
		});
	}, [checkpointsResult?.data, filters.sorting]);

	const columns = React.useMemo(
		() => [
			{
				key: "name",
				label: t(
					"access.attendees.attendeesList.batchActions.checklistsTable.columns.name"
				),
				render: (_, checkpoint) => (
					<TextTableCell title={checkpoint.name} />
				),
				sortable: true,
				width: "320px"
			},
			{
				key: "defaultNbAccess",
				label: t(
					"access.attendees.attendeesList.batchActions.checklistsTable.columns.defaultNbAccess"
				),
				render: (_, checkpoint) => {
					const isUsingStrictMode = checkpoint.isUsingStrictMode;
					let count;
					if (isUsingStrictMode) {
						count = checkpoint.strictNbAccess;
					} else {
						count =
							checkpoint.defaultNbAccess ??
							checkpoint.strictNbAccess;
					}

					const isUnlimited = isNumberOfAccessUnlimited(count);

					if (isUnlimited) {
						return (
							<TextTableCell
								title={t(
									"access.attendees.attendeesList.batchActions.unlimited"
								)}
							/>
						);
					}

					const translationKey =
						count === 1
							? "access.attendees.attendeesList.batchActions.checklistsTable.entrance"
							: "access.attendees.attendeesList.batchActions.checklistsTable.entrances";
					const displayText =
						count != null ? `${count} ${t(translationKey)}` : "-";
					return <TextTableCell title={displayText} />;
				},
				width: "150px"
			},
			{
				key: "status",
				label: t(
					"access.attendees.attendeesList.batchActions.checklistsTable.columns.status"
				),
				render: (_, checkpoint) => (
					<Status status={checkpoint.status} />
				),
				sortable: true,
				width: "100px",
				align: "right"
			}
		],
		[t]
	);

	return (
		<>
			<FilterSetController
				loading={isLoading}
				total={checkpointsResult?.totalCount || 0}
				labelTotal={t("access.checkpoint.checkpointsUpper")}
				search={{
					value: filters.search,
					onChange: (_, value) =>
						setFilters(prev => ({ ...prev, search: value || "" })),
					handleReset: () =>
						setFilters(prev => ({ ...prev, search: "" })),
					displayResetIcon: true,
					placeholder: t(
						"access.attendees.attendeesList.batchActions.checklistsTable.searchPlaceholder"
					)
				}}
				onChange={() => {}}
			/>
			<SmartTable
				minWidthDisabled
				rowKey="key"
				columns={columns}
				data={sortedData || []}
				loading={isLoading}
				rowSelection={{
					selected: filters.selectedCheckpoints,
					onChangeSelection: selectedCheckpoints =>
						setFilters(prev => ({ ...prev, selectedCheckpoints }))
				}}
				currentSorting={filters.sorting}
				onClickSorting={sorting =>
					setFilters(prev => ({ ...prev, sorting }))
				}
			/>
		</>
	);
};

ChecklistsTable.propTypes = {
	filters: PropTypes.shape({
		search: PropTypes.string,
		sorting: PropTypes.object,
		selectedCheckpoints: PropTypes.arrayOf(PropTypes.number)
	}).isRequired,
	setFilters: PropTypes.func.isRequired,
	eventId: PropTypes.number,
	disabled: PropTypes.bool
};

ChecklistsTable.defaultProps = {
	eventId: null,
	disabled: false
};

export default ChecklistsTable;
